import {
  BellRing, CalendarClock, CalendarOff, CarFront, ClipboardCheck, Coins, Contact, FlaskConical, FolderInput, HeartPulse, House, IdCard, ListChecks, SlidersHorizontal,
  TreePalm, Signature, Stethoscope, TrafficCone, UserMinus, UserPlus, Wallet,
} from 'lucide-react'
import type { NavSection } from '../../core/router/navConfig'

/** Navegação da área DP. Em 24/10/2026 era só a página inicial; em 26/10/2026 entram
 *  os módulos da camada 1, cada um com a própria key, pelo documento de entendimento
 *  de 22/10/2026. Ver docs/areas/05-como-adicionar.md.
 *
 *  Desde 27/10/2026 (Amorim) o menu se divide pela função de quem cuida de cada rotina
 *  no DP: ponto e benefícios, movimentações do colaborador, saúde e segurança do
 *  trabalho. Afastamentos e Contratações entram em Movimentações; Bônus e adicionais,
 *  em Ponto e benefícios.
 *
 *  O Início pede qualquer leitura do DP: quem tem a área sem key nenhuma não cai numa
 *  página vazia, e a área nem vira card para essa pessoa. Arquivos pede qualquer key
 *  que mande um dos arquivos, a mesma lista do servidor. */
export const dpNav: NavSection[] = [
  {
    title: 'Visão geral',
    titleKey: 'nav.section.dpOverview',
    items: [
      { label: 'Início', labelKey: 'nav.home', path: '/dp', icon: House, ready: true, anyPermission: [
        'dp_ponto.view', 'dp_vale_alimentacao.view', 'dp_ferias.view', 'dp_desligamento.view', 'dp_plano_saude.view',
        'dp_exame.view', 'dp_cnh.view', 'dp_multa.view',
        // Os módulos de 31/10/2026.
        'dp_toxicologico.view', 'dp_bonus.view', 'dp_contratacao.view', 'dp_rotina.view',
        // Os módulos de 01/11/2026.
        'dp_ausencia.view', 'dp_parametro.view', 'dp_colaborador.view', 'dp_sinistro.view', 'dp_folha.view',
        // Os destinatários de alerta, de 02/11/2026.
        'dp_alerta.view',
      ] },
      { label: 'Arquivos', labelKey: 'nav.dpFiles', path: '/dp/arquivos', icon: FolderInput, ready: true, anyPermission: [
        'dp_plano_saude.edit', 'dp_ponto.edit', 'dp_desligamento.edit', 'dp_ferias.edit', 'dp_cnh.edit', 'dp_exame.edit',
        'dp_toxicologico.edit', 'dp_contratacao.edit', 'dp_rotina.edit', 'dp_folha.edit',
      ] },
      { label: 'Rotinas da folha', labelKey: 'nav.dpRoutines', path: '/dp/rotinas', icon: ListChecks, ready: true, permission: 'dp_rotina.view' },
      { label: 'Conferência da folha', labelKey: 'nav.dpPayrollCheck', path: '/dp/conferencia-da-folha', icon: ClipboardCheck, ready: true, permission: 'dp_folha.view' },
      { label: 'Histórico do colaborador', labelKey: 'nav.dpEmployeeHistory', path: '/dp/colaboradores', icon: Contact, ready: true, permission: 'dp_colaborador.view' },
      { label: 'Parâmetros', labelKey: 'nav.dpParameters', path: '/dp/parametros', icon: SlidersHorizontal, ready: true, permission: 'dp_parametro.view' },
      // Os grupos de e-mail que recebem os alertas do DP (02/11/2026), pedido do Amorim.
      { label: 'Destinatários de alerta', labelKey: 'nav.dpAlertRecipients', path: '/dp/alertas', icon: BellRing, ready: true, permission: 'dp_alerta.view' },
    ],
  },
  {
    title: 'Ponto e benefícios',
    titleKey: 'nav.section.dpTimeBenefits',
    items: [
      { label: 'Assinatura do ponto', labelKey: 'nav.timesheetSignature', path: '/dp/assinatura-do-ponto', icon: Signature, ready: true, permission: 'dp_ponto.view' },
      { label: 'Vale-alimentação', labelKey: 'nav.mealAllowance', path: '/dp/vale-alimentacao', icon: Wallet, ready: true, permission: 'dp_vale_alimentacao.view' },
      { label: 'Plano de saúde', labelKey: 'nav.dpHealthPlan', path: '/dp/plano-de-saude', icon: HeartPulse, ready: true, permission: 'dp_plano_saude.view' },
      { label: 'Bônus e adicionais', labelKey: 'nav.dpBonus', path: '/dp/bonus', icon: Coins, ready: true, permission: 'dp_bonus.view' },
    ],
  },
  {
    title: 'Movimentações',
    titleKey: 'nav.section.dpMovements',
    items: [
      { label: 'Férias', labelKey: 'nav.dpVacations', path: '/dp/ferias', icon: TreePalm, ready: true, permission: 'dp_ferias.view' },
      { label: 'Ausências', labelKey: 'nav.dpAbsences', path: '/dp/ausencias', icon: CalendarOff, ready: true, permission: 'dp_ausencia.view' },
      { label: 'Desligamentos', labelKey: 'nav.dpTerminations', path: '/dp/desligamentos', icon: UserMinus, ready: true, permission: 'dp_desligamento.view' },
      { label: 'Contratações', labelKey: 'nav.dpHiring', path: '/dp/contratacoes', icon: UserPlus, ready: true, permission: 'dp_contratacao.view' },
    ],
  },
  {
    title: 'Saúde e segurança',
    titleKey: 'nav.section.dpHealthSafety',
    items: [
      { label: 'Exames', labelKey: 'nav.dpExams', path: '/dp/exames', icon: Stethoscope, ready: true, permission: 'dp_exame.view' },
      { label: 'Toxicológico', labelKey: 'nav.dpToxicology', path: '/dp/toxicologico', icon: FlaskConical, ready: true, permission: 'dp_toxicologico.view' },
      { label: 'CNH', labelKey: 'nav.dpLicense', path: '/dp/cnh', icon: IdCard, ready: true, permission: 'dp_cnh.view' },
      // Os prazos das três telas acima juntos, em calendário e linha do tempo (04/11/2026),
      // para quem lê qualquer uma delas; cada tipo só vem para quem tem a key dele.
      { label: 'Prazos', labelKey: 'nav.dpHealthDeadlines', path: '/dp/saude-e-seguranca/prazos', icon: CalendarClock, ready: true, anyPermission: [
        'dp_exame.view', 'dp_toxicologico.view', 'dp_cnh.view',
      ] },
    ],
  },
  {
    // As multas (27/10/2026) e os sinistros (01/11/2026) que o Abastecimento lança. O DP
    // só lê: o lançamento é do setor de combustível, na área dele.
    title: 'Descontos',
    titleKey: 'nav.section.dpDeductions',
    items: [
      { label: 'Multas', labelKey: 'nav.fines', path: '/dp/multas', icon: TrafficCone, ready: true, permission: 'dp_multa.view' },
      { label: 'Sinistros', labelKey: 'nav.claims', path: '/dp/sinistros', icon: CarFront, ready: true, permission: 'dp_sinistro.view' },
    ],
  },
]
